import React, { useState, useMemo } from 'react';
import { CreditCard, Lock, Tag, Gift, TrendingUp, Percent } from 'lucide-react';
import { useGalleryCart } from '../context/GalleryCartContext';
import { useGalleryTheme } from '../context/GalleryThemeContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useGalleryConfig } from '../context/GalleryConfigContext';
import { PricingService } from '../../../services/pricingService';
import Button from '../components/common/Button';

type StripeCheckout = {
  redirectToCheckout: (options: { sessionId: string }) => Promise<{ error?: { message?: string } }>;
};

const GalleryCheckoutPage: React.FC = () => {
  const { cart, applyCoupon, removeCoupon } = useGalleryCart();
  const { theme } = useGalleryTheme();
  const { customer } = useCustomerAuth();
  const { config } = useGalleryConfig();
  const [processing, setProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);
  const [applyingPromo, setApplyingPromo] = useState(false);

  // Calculate dynamic pricing for individual items
  const itemsPricing = useMemo(() => {
    const totalIndividualPhotos = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    if (totalIndividualPhotos === 0) return null;
    return PricingService.calculatePrice(totalIndividualPhotos);
  }, [cart.items]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cart.currency,
    }).format(price);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Create Stripe checkout session with dynamic pricing
      const response = await fetch('/checkout/create-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cart,
          customerId: customer?.id,
          photographerId: config?.photographerId,
          returnUrl: window.location.origin,
        }),
      });

      if (response.status === 409) {
        setCheckoutError(
          'Purchasing is temporarily unavailable for this gallery. Please check back soon.'
        );
        return;
      }

      if (response.ok) {
        const { sessionId } = await response.json();

        if (!sessionId) {
          setCheckoutError('Unable to start checkout. Please try again.');
          return;
        }

        // Redirect to Stripe Checkout. Cart/order state is updated only after Stripe confirms payment.
        const stripe = (window as Window & { Stripe?: StripeCheckout }).Stripe;
        if (!stripe) {
          setCheckoutError('Payment processing is unavailable. Please try again later.');
          return;
        }

        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          setCheckoutError(error.message || 'Unable to start checkout. Please try again.');
        }
      } else {
        setCheckoutError('Unable to start checkout. Please try again.');
      }
    } catch {
      setCheckoutError('Unable to start checkout. Please check your connection and try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;

    setApplyingPromo(true);
    setPromoError(null);

    try {
      await applyCoupon(promoCode.trim());
      setPromoCode('');
    } catch (error: unknown) {
      setPromoError(error instanceof Error ? error.message : 'Invalid promo code');
    } finally {
      setApplyingPromo(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.text.primary }}>
          Checkout
        </h1>
        <p style={{ color: theme.colors.text.secondary }}>Complete your order securely</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Payment Form */}
        <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Information */}
            <div
              className="p-6 rounded-lg border"
              style={{
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.border,
              }}
            >
              <h3
                className="text-lg font-semibold mb-4"
                style={{ color: theme.colors.text.primary }}
              >
                Customer Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    defaultValue={customer?.name}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      borderColor: theme.colors.border,
                      backgroundColor: theme.colors.surface,
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    defaultValue={customer?.email}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      borderColor: theme.colors.border,
                      backgroundColor: theme.colors.surface,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div
              className="p-6 rounded-lg border"
              style={{
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.border,
              }}
            >
              <h3
                className="text-lg font-semibold mb-4 flex items-center"
                style={{ color: theme.colors.text.primary }}
              >
                <CreditCard size={20} className="mr-2" />
                Payment Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Card Number</label>
                  <input
                    type="text"
                    placeholder="1234 5678 9012 3456"
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                    style={{
                      borderColor: theme.colors.border,
                      backgroundColor: theme.colors.surface,
                    }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Expiry Date</label>
                    <input
                      type="text"
                      placeholder="MM/YY"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                      style={{
                        borderColor: theme.colors.border,
                        backgroundColor: theme.colors.surface,
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">CVC</label>
                    <input
                      type="text"
                      placeholder="123"
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                      style={{
                        borderColor: theme.colors.border,
                        backgroundColor: theme.colors.surface,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Checkout Error */}
            {checkoutError && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                {checkoutError}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={processing}
              leftIcon={<Lock size={20} />}
            >
              {processing ? 'Processing...' : `Complete Order - ${formatPrice(cart.total)}`}
            </Button>
          </form>
        </div>

        {/* Order Summary */}
        <div>
          <div
            className="p-6 rounded-lg border sticky top-4"
            style={{
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
            }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: theme.colors.text.primary }}>
              Order Summary
            </h3>

            {/* Items */}
            <div className="space-y-3 mb-4">
              {cart.items.map(item => (
                <div key={item.id} className="flex justify-between">
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.colors.text.primary }}>
                      {item.photo.title || `Photo ${item.photoId}`}
                    </p>
                    <p className="text-xs" style={{ color: theme.colors.text.muted }}>
                      {item.resolution} × {item.quantity}
                    </p>
                  </div>
                  <span style={{ color: theme.colors.text.primary }}>
                    {formatPrice(item.price * item.quantity)}
                  </span>
                </div>
              ))}

              {cart.packages.map(pkg => (
                <div key={pkg.id} className="flex justify-between">
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.colors.text.primary }}>
                      {pkg.packageName}
                    </p>
                    <p className="text-xs" style={{ color: theme.colors.text.muted }}>
                      {pkg.photoCount} photos
                    </p>
                    <p className="text-xs text-green-600">
                      Save {formatPrice(pkg.originalPrice - pkg.price)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs line-through" style={{ color: theme.colors.text.muted }}>
                      {formatPrice(pkg.originalPrice)}
                    </p>
                    <p style={{ color: theme.colors.text.primary }}>{formatPrice(pkg.price)}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Promo Code Section */}
            {!cart.couponCode && (
              <div
                className="mb-4 p-3 rounded-lg border-2 border-dashed"
                style={{ borderColor: theme.colors.border }}
              >
                <div
                  className="text-sm font-medium mb-2"
                  style={{ color: theme.colors.text.primary }}
                >
                  Have a promo code?
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value.toUpperCase())}
                    className="flex-1 px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2"
                    style={{
                      borderColor: theme.colors.border,
                      backgroundColor: theme.colors.surface,
                    }}
                    disabled={applyingPromo}
                  />
                  <Button
                    size="sm"
                    onClick={handleApplyPromo}
                    loading={applyingPromo}
                    disabled={!promoCode.trim()}
                  >
                    Apply
                  </Button>
                </div>
                {promoError && <p className="text-xs text-red-500 mt-1">{promoError}</p>}
              </div>
            )}

            {/* Totals */}
            <div
              className="border-t pt-4 space-y-2"
              style={{ borderTopColor: theme.colors.border }}
            >
              <div className="flex justify-between">
                <span style={{ color: theme.colors.text.secondary }}>Subtotal</span>
                <span style={{ color: theme.colors.text.primary }}>
                  {formatPrice(cart.subtotal)}
                </span>
              </div>

              {/* Dynamic Volume Discount */}
              {itemsPricing && itemsPricing.volumeDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <div className="flex items-center gap-1">
                    <TrendingUp size={12} />
                    <span className="text-sm">Volume Discount</span>
                  </div>
                  <span>-{formatPrice(itemsPricing.volumeDiscount)}</span>
                </div>
              )}

              {/* Package Savings */}
              {cart.packages.length > 0 && (
                <div className="flex justify-between text-green-600">
                  <div className="flex items-center gap-1">
                    <Gift size={12} />
                    <span className="text-sm">Bundle Savings</span>
                  </div>
                  <span>
                    -
                    {formatPrice(
                      cart.packages.reduce((sum, pkg) => sum + (pkg.originalPrice - pkg.price), 0)
                    )}
                  </span>
                </div>
              )}

              {/* Promo Code Discount */}
              {cart.couponCode && cart.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <div className="flex items-center gap-1">
                    <Tag size={12} />
                    <span className="text-sm">Promo: {cart.couponCode}</span>
                    <button onClick={removeCoupon} className="ml-1 text-red-500 hover:text-red-600">
                      ×
                    </button>
                  </div>
                  <span>-{formatPrice(cart.discount)}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span style={{ color: theme.colors.text.secondary }}>Tax</span>
                <span style={{ color: theme.colors.text.primary }}>{formatPrice(cart.tax)}</span>
              </div>

              {/* Total Savings Summary */}
              {(itemsPricing?.savings ||
                cart.packages.reduce((sum, pkg) => sum + (pkg.originalPrice - pkg.price), 0) ||
                cart.discount) > 0 && (
                <div
                  className="p-3 rounded-lg text-center"
                  style={{
                    backgroundColor: `${theme.colors.success}15`,
                    color: theme.colors.success,
                  }}
                >
                  <div className="flex items-center justify-center gap-1 text-sm font-semibold">
                    <Percent size={14} />
                    <span>
                      You're saving{' '}
                      {formatPrice(
                        (itemsPricing?.savings || 0) +
                          cart.packages.reduce(
                            (sum, pkg) => sum + (pkg.originalPrice - pkg.price),
                            0
                          ) +
                          cart.discount
                      )}
                      !
                    </span>
                  </div>
                </div>
              )}

              <div
                className="border-t pt-2 flex justify-between font-semibold"
                style={{ borderTopColor: theme.colors.border }}
              >
                <span style={{ color: theme.colors.text.primary }}>Total</span>
                <span style={{ color: theme.colors.text.primary }}>{formatPrice(cart.total)}</span>
              </div>
            </div>

            {/* Security Note */}
            <div
              className="mt-6 p-3 rounded text-xs text-center"
              style={{ backgroundColor: theme.colors.background }}
            >
              <Lock size={16} className="inline mr-1" />
              Your payment information is secure and encrypted
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryCheckoutPage;
